import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Text } from "@ui-kitten/components";
import Dialog, { DialogProps } from "components/Dialog";
import env from "config/env";
import { EventDetails, SeatDetails } from "features/types";
import { useAppSelector } from "hooks/useAppSelector";
import useCurrentUser from "hooks/useCurrentUser";
import useShare from "hooks/useShare";
import i18n from "plugins/localization";
import React, { useMemo, useState } from "react";
import { Dimensions, View } from "react-native";
import { formatEventDate } from "utils/eventUtils";
import { getUserName } from "utils/userUtils";

interface GroupDialogProps extends DialogProps {
  seat: SeatDetails;
  event: EventDetails;
  groupCode: string;
}

const GroupDialog: React.FC<GroupDialogProps> = ({
  visible,
  setVisible = () => {},
  seat,
  event,
  groupCode,
  ...rest
}) => {
  const [showUrl, setShowUrl] = useState(false);
  const { tenantAlias, opponentAlias, code } = event;
  const { shareError, copied, handleShare } = useShare();
  const tenantSubdomain = useAppSelector(
    (state) => state.tenant.tenantSubdomain
  );
  const { currentUser } = useCurrentUser();

  const path = "group_reservation";
  if (!currentUser) return null;

  const resolveNativeUrl = () => {
    const base = env.rootUrl.replace("tenantSubdomain", tenantSubdomain!);
    return `${base}/${path}/${code}/${groupCode}`;
  };

  const message = useMemo(() => {
    if (!currentUser) return "";
    return i18n.t("screens.EventDetail.groupShareMessage", {
      userName: getUserName(currentUser),
      opponentAlias,
      tenantAlias,
      eventDate: formatEventDate(event),
      userFirstName: currentUser.firstName,
      link: resolveNativeUrl(),
    });
  }, [currentUser]);

  const handleShareBtnPress = async () => {
    setShowUrl(true);
    handleShare(message);
  };

  if (!currentUser) return null;

  return (
    <Dialog
      visible={visible}
      setVisible={setVisible}
      {...rest}
      cancelText="CLOSE"
      okText={
        copied ? i18n.t("common.copiedButton") : i18n.t("common.shareButton")
      }
      cancelBtnProps={{ testID: "group-reservation-cancel-button" }}
      okBtnProps={{
        status: "info",
        testID: "group-reservation-share-button",
      }}
      onOk={handleShareBtnPress}
      style={{ maxWidth: 375, width: Dimensions.get("screen").width * 0.8 }}
    >
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text category="h6">Sit with friends</Text>
        <MaterialCommunityIcons
          onPress={() => setVisible(false)}
          name="close"
          size={18}
          color="black"
        />
      </View>
      <Text category="c1" style={{ marginVertical: 15 }}>
        Share your reservation with other members, so they can book seats next
        to you.
      </Text>
      <Text category="c2" style={{ marginBottom: 15 }}>
        Important Note: We may need to relocate your reservation within the same
        section to accommodate your larger group.
      </Text>
      {!event.allowGroupCodeReleases && (
        <Text style={{ marginBottom: 15 }} category="c2" status="danger">
          {i18n.t("screens.EventDetail.groupReservationCutOff")}
        </Text>
      )}
      {showUrl && <Text selectable>{`${resolveNativeUrl()}`}</Text>}
      {shareError && (
        <Text style={{ marginTop: 10 }} status="danger">
          {i18n.t("common.shareNotSupportedWarning")}
        </Text>
      )}
    </Dialog>
  );
};

export default GroupDialog;
